import { useState, useEffect } from 'react';
import { Card, KpiCard, Badge, SlidePanel, PanelSection, SpecGrid, SpecCell, Modal, FormSelect, toast } from '@/components';
import { useApi, triggerRefetch } from '@/api/hooks';
import { api } from '@/api/client';
import { useRole } from '@/hooks/useRole';
import styles from './Contacts.module.css';

interface Plan {
  id: number;
  name: string;
  price_monthly: number;
}

interface Contact {
  id: string;
  name: string;
  email: string;
  company: string | null;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface Reply {
  id: string;
  contact_id: string;
  admin_name: string;
  admin_email: string;
  body: string;
  created_at: string;
}

function buildMailtoUrl(contact: Contact): string {
  const subject = encodeURIComponent(`Re: お問い合わせへのご回答 — 住縁 Mirai EMS`);
  const body = encodeURIComponent(
    `${contact.name} 様\n\nお問い合わせいただきありがとうございます。\n\n\n\n---\n元のお問い合わせ内容:\n${contact.message}\n\n— 住縁 Mirai EMS サポートチーム`
  );
  return `mailto:${contact.email}?subject=${subject}&body=${body}`;
}

function buildOutlookWebUrl(contact: Contact): string {
  const subject = encodeURIComponent(`Re: お問い合わせへのご回答 — 住縁 Mirai EMS`);
  const body = encodeURIComponent(
    `${contact.name} 様\n\nお問い合わせいただきありがとうございます。\n\n\n\n---\n元のお問い合わせ内容:\n${contact.message}\n\n— 住縁 Mirai EMS サポートチーム`
  );
  return `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(contact.email)}&subject=${subject}&body=${body}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function Contacts() {
  const { isAdmin } = useRole();
  const { data: contacts, loading, refetch } = useApi<Contact[]>('/console/contacts');

  const [selected, setSelected] = useState<Contact | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [showConvert, setShowConvert] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertPlanId, setConvertPlanId] = useState<string>('');
  const { data: plans } = useApi<Plan[]>('/console/plans');

  // 返信関連
  const [replies, setReplies] = useState<Reply[]>([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const list = (contacts ?? []).filter(c => {
    if (filter === 'unread') return !c.is_read;
    if (filter === 'read') return c.is_read;
    return true;
  });

  const total = contacts?.length ?? 0;
  const unread = contacts?.filter(c => !c.is_read).length ?? 0;
  const read = contacts?.filter(c => c.is_read).length ?? 0;

  const kpis = [
    { label: '総件数', value: String(total), delta: '', trend: 'flat' as const },
    { label: '未読', value: String(unread), delta: unread > 0 ? '要確認' : '', trend: unread > 0 ? 'down' as const : 'flat' as const },
    { label: '既読', value: String(read), delta: '', trend: 'flat' as const },
  ];

  // 選択変更時に返信履歴を取得
  useEffect(() => {
    if (!selected) { setReplies([]); return; }
    setRepliesLoading(true);
    api<Reply[]>(`/console/contacts/${selected.id}/replies`)
      .then(setReplies)
      .catch(() => setReplies([]))
      .finally(() => setRepliesLoading(false));
  }, [selected?.id]);

  const handleMarkRead = async (c: Contact) => {
    if (c.is_read) return;
    try {
      await api(`/console/contacts/${c.id}/read`, { method: 'PATCH' });
      refetch();
      if (selected?.id === c.id) setSelected({ ...c, is_read: true });
    } catch { toast('既読にできませんでした', 'error'); }
  };

  const handleReply = async () => {
    if (!selected || !replyText.trim()) return;
    setSending(true);
    try {
      const res = await api<{ success: boolean; reply: Reply }>(`/console/contacts/${selected.id}/reply`, {
        method: 'POST',
        body: JSON.stringify({ body: replyText.trim() }),
      });
      setReplies(prev => [...prev, res.reply]);
      setReplyText('');
      toast('返信を送信しました', 'success');
      refetch();
      setSelected({ ...selected, is_read: true });
    } catch { toast('返信の送信に失敗しました', 'error'); }
    finally { setSending(false); }
  };

  const handleConvertToLead = async () => {
    if (!selected) return;
    setConverting(true);
    try {
      await api(`/console/contacts/${selected.id}/to-lead`, {
        method: 'POST',
        body: JSON.stringify({
          notes: `問い合わせから変換: ${selected.message.substring(0, 50)}`,
          ...(convertPlanId ? { plan_interest: Number(convertPlanId) } : {}),
        }),
      });
      toast('リードに変換しました', 'success');
      setShowConvert(false);
      setConvertPlanId('');
      refetch();
      triggerRefetch('/console/leads');
      setSelected({ ...selected, is_read: true });
    } catch { toast('変換に失敗しました', 'error'); }
    finally { setConverting(false); }
  };

  const openDetail = (c: Contact) => {
    setSelected(c);
    setReplyText('');
    if (!c.is_read) handleMarkRead(c);
  };

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.kpiRow}>
        {kpis.map(k => <KpiCard key={k.label} {...k} />)}
      </div>

      <Card title="問い合わせ一覧" actions={
        <select className={styles.filterSelect} value={filter} onChange={e => setFilter(e.target.value as 'all' | 'unread' | 'read')}>
          <option value="all">全て</option>
          <option value="unread">未読のみ</option>
          <option value="read">既読のみ</option>
        </select>
      }>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>状態</th>
              <th className={styles.th}>名前</th>
              <th className={styles.th}>メール</th>
              <th className={styles.th}>会社名</th>
              <th className={styles.th}>メッセージ</th>
              <th className={styles.th}>日時</th>
              <th className={styles.th}>操作</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td className={styles.emptyRow} colSpan={7}>問い合わせがありません</td></tr>
            ) : list.map(c => (
              <tr key={c.id} style={{ cursor: 'pointer', background: c.is_read ? undefined : 'var(--brand-dim)' }} onClick={() => openDetail(c)}>
                <td className={styles.td}>
                  {!c.is_read && <span className={styles.unreadDot} />}
                  <Badge variant={c.is_read ? 'gray' : 'blue'}>{c.is_read ? '既読' : '未読'}</Badge>
                </td>
                <td className={styles.td}><span className={styles.nameCell}>{c.name}</span></td>
                <td className={styles.td}><span className={styles.emailCell}>{c.email}</span></td>
                <td className={styles.td}><span className={styles.companyCell}>{c.company ?? '—'}</span></td>
                <td className={styles.td}><span className={styles.msgPreview}>{c.message}</span></td>
                <td className={styles.td}><span className={styles.timeCell}>{formatDate(c.created_at)}</span></td>
                <td className={styles.td}>
                  <div className={styles.actions} onClick={e => e.stopPropagation()}>
                    <button className={styles.btnSm} onClick={() => openDetail(c)}>詳細</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* 詳細パネル */}
      <SlidePanel open={!!selected} title="問い合わせ詳細" onClose={() => setSelected(null)}>
        {selected && (
          <>
            <PanelSection title="送信者情報">
              <SpecGrid>
                <SpecCell label="名前">{selected.name}</SpecCell>
                <SpecCell label="メール">{selected.email}</SpecCell>
                <SpecCell label="会社名">{selected.company ?? '—'}</SpecCell>
                <SpecCell label="受信日時">{formatDate(selected.created_at)}</SpecCell>
                <SpecCell label="状態">
                  <Badge variant={selected.is_read ? 'gray' : 'blue'}>{selected.is_read ? '既読' : '未読'}</Badge>
                </SpecCell>
              </SpecGrid>
            </PanelSection>

            <PanelSection title="メッセージ">
              <div className={styles.msgFull}>{selected.message}</div>
            </PanelSection>

            {/* 返信履歴 */}
            <PanelSection title={`返信履歴（${replies.length}件）`}>
              {repliesLoading ? (
                <div style={{ fontSize: 12, color: 'var(--ink3)', padding: 8 }}>読み込み中...</div>
              ) : replies.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--ink3)', padding: 8 }}>返信なし</div>
              ) : (
                <div className={styles.replyList}>
                  {replies.map(r => (
                    <div key={r.id} className={styles.replyItem}>
                      <div className={styles.replyHeader}>
                        <span className={styles.replyAdmin}>{r.admin_name}</span>
                        <span className={styles.replyTime}>{formatDate(r.created_at)}</span>
                      </div>
                      <div className={styles.replyBody}>{r.body}</div>
                    </div>
                  ))}
                </div>
              )}
            </PanelSection>

            {/* 返信フォーム */}
            {isAdmin && (
              <PanelSection title="返信する">
                <textarea
                  className={styles.replyTextarea}
                  rows={4}
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder={`${selected.name} さんへの返信を入力...`}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button
                    className={styles.btnPrimary}
                    disabled={sending || !replyText.trim()}
                    onClick={handleReply}
                  >
                    {sending ? '送信中...' : '返信を送信'}
                  </button>
                  <a
                    className={styles.btnOutlook}
                    href={buildOutlookWebUrl(selected)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Outlook Web
                  </a>
                  <a
                    className={styles.btnMailto}
                    href={buildMailtoUrl(selected)}
                  >
                    メールアプリ
                  </a>
                  <button className={styles.btnSm} onClick={() => setShowConvert(true)}>リードに変換</button>
                </div>
              </PanelSection>
            )}
          </>
        )}
      </SlidePanel>

      {/* リード変換確認モーダル */}
      <Modal
        open={showConvert}
        title="リードに変換"
        onClose={() => setShowConvert(false)}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className={styles.btnSm} onClick={() => setShowConvert(false)}>キャンセル</button>
            <button className={styles.btnPrimary} disabled={converting} onClick={handleConvertToLead}>
              {converting ? '変換中...' : '変換する'}
            </button>
          </div>
        }
      >
        <p style={{ fontSize: 13, lineHeight: 1.6 }}>
          <strong>{selected?.name}</strong>（{selected?.email}）をリード管理に変換しますか？
        </p>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>希望プラン（任意）</label>
          <FormSelect
            value={convertPlanId}
            onChange={e => setConvertPlanId(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">未設定</option>
            {(plans ?? []).filter(p => p.id !== 1).map(p => (
              <option key={p.id} value={p.id}>{p.name}（¥{p.price_monthly.toLocaleString()}/月）</option>
            ))}
          </FormSelect>
          <p style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
            不明な場合は未設定のまま変換し、リード管理画面で後から設定できます。
          </p>
        </div>
      </Modal>
    </div>
  );
}
